import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    IconButton,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Divider,
    Button,
    Breadcrumbs,
    Link,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PersonIcon from '@mui/icons-material/Person';
import PaymentIcon from '@mui/icons-material/Payment';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import { Title, useNotify } from 'react-admin';

// Ensure API_URL ends with /api
const baseApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_URL = baseApiUrl.endsWith('/api') ? baseApiUrl : `${baseApiUrl}/api`;

interface OrderItem {
    id: number;
    title: string;
    priceType: string;
    unitPrice: string;
    quantity: number;
    discount: string;
    total: string;
    productId: number | null;
}

interface Order {
    id: number;
    uuid: string;
    orderNumber: string;
    status: string;
    paymentStatus: string;
    customerName: string;
    customerEmail: string;
    subtotal: string;
    discount: string;
    tax: string;
    total: string;
    currency: string;
    items: OrderItem[];
    createdAt: string;
    paidAt: string | null;
    completedAt: string | null;
}

interface Invoice {
    id: number;
    uuid: string;
    invoiceNumber: string;
    type: string;
    status: string;
    orderId: number;
    orderNumber: string;
    sellerName: string;
    buyerName: string;
    buyerEmail: string;
    subtotal: string;
    discountTotal: string;
    taxRate: string;
    taxTotal: string;
    total: string;
    currency: string;
    hasPdf: boolean;
    issuedAt: string | null;
    paidAt: string | null;
    createdAt: string;
}

const statusConfig: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
    pending: { label: 'Ausstehend', color: 'warning' },
    paid: { label: 'Bezahlt', color: 'info' },
    completed: { label: 'Abgeschlossen', color: 'success' },
    cancelled: { label: 'Storniert', color: 'error' },
    refunded: { label: 'Erstattet', color: 'secondary' },
};

const paymentStatusConfig: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
    pending: { label: 'Ausstehend', color: 'warning' },
    processing: { label: 'In Bearbeitung', color: 'info' },
    completed: { label: 'Bezahlt', color: 'success' },
    failed: { label: 'Fehlgeschlagen', color: 'error' },
};

const formatCurrency = (amount: string | number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency,
    }).format(typeof amount === 'string' ? parseFloat(amount) : amount);
};

const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Intl.DateTimeFormat('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(dateString));
};

// Order List Component
const OrdersList: React.FC<{ onSelectOrder: (id: number) => void }> = ({ onSelectOrder }) => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState('');

    const loadOrders = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            let url = `${API_URL}/order-management/company`;
            if (statusFilter) {
                url += `?status=${statusFilter}`;
            }

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setOrders(data.orders || []);
            }
        } catch (error) {
            console.error('Failed to load orders:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadOrders();
    }, [statusFilter]);

    const filteredOrders = orders.filter(order => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            order.orderNumber.toLowerCase().includes(query) ||
            order.customerName.toLowerCase().includes(query) ||
            order.customerEmail.toLowerCase().includes(query)
        );
    });

    return (
        <Box>
            <Title title="Bestellungen" />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShoppingCartIcon /> Bestellungen
                </Typography>
                <Button startIcon={<RefreshIcon />} onClick={loadOrders} disabled={loading}>
                    Aktualisieren
                </Button>
            </Box>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                                fullWidth
                                label="Suche"
                                placeholder="Bestellnr., Name, E-Mail..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                size="small"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    label="Status"
                                >
                                    <MenuItem value="">Alle</MenuItem>
                                    <MenuItem value="pending">Ausstehend</MenuItem>
                                    <MenuItem value="paid">Bezahlt</MenuItem>
                                    <MenuItem value="completed">Abgeschlossen</MenuItem>
                                    <MenuItem value="cancelled">Storniert</MenuItem>
                                    <MenuItem value="refunded">Erstattet</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : filteredOrders.length === 0 ? (
                <Card>
                    <CardContent>
                        <Typography color="text.secondary" align="center">
                            Keine Bestellungen gefunden
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Bestellnummer</TableCell>
                                <TableCell>Kunde</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Zahlung</TableCell>
                                <TableCell align="right">Betrag</TableCell>
                                <TableCell>Datum</TableCell>
                                <TableCell align="center">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredOrders.map((order) => (
                                <TableRow key={order.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            {order.orderNumber}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{order.customerName}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {order.customerEmail}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={statusConfig[order.status]?.label || order.status}
                                            color={statusConfig[order.status]?.color || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={paymentStatusConfig[order.paymentStatus]?.label || order.paymentStatus}
                                            color={paymentStatusConfig[order.paymentStatus]?.color || 'default'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight="bold">
                                            {formatCurrency(order.total, order.currency)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{formatDate(order.createdAt)}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            onClick={() => onSelectOrder(order.id)}
                                            title="Details anzeigen"
                                        >
                                            <VisibilityIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

// Order Detail Component
const OrderDetail: React.FC<{ orderId: number; onBack: () => void }> = ({ orderId, onBack }) => {
    const [order, setOrder] = useState<Order | null>(null);
    const [loading, setLoading] = useState(true);
    const [invoice, setInvoice] = useState<Invoice | null>(null);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [creatingInvoice, setCreatingInvoice] = useState(false);
    const notify = useNotify();

    const loadInvoice = async () => {
        try {
            setInvoiceLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/invoices/order/${orderId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setInvoice(data.invoice);
            } else if (response.status === 404) {
                setInvoice(null);
            }
        } catch (error) {
            console.error('Failed to load invoice:', error);
        } finally {
            setInvoiceLoading(false);
        }
    };

    const createInvoice = async () => {
        try {
            setCreatingInvoice(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/invoices/order/${orderId}/create`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const data = await response.json();
                setInvoice(data.invoice);
                notify('Rechnung erfolgreich erstellt', { type: 'success' });
            } else {
                const errorData = await response.json();
                notify(errorData.detail || 'Fehler beim Erstellen der Rechnung', { type: 'error' });
            }
        } catch (error) {
            console.error('Failed to create invoice:', error);
            notify('Fehler beim Erstellen der Rechnung', { type: 'error' });
        } finally {
            setCreatingInvoice(false);
        }
    };

    const downloadPdf = async () => {
        if (!invoice) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/invoices/${invoice.id}/pdf`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${invoice.invoiceNumber}.pdf`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                notify('Fehler beim Herunterladen der PDF', { type: 'error' });
            }
        } catch (error) {
            console.error('Failed to download PDF:', error);
            notify('Fehler beim Herunterladen der PDF', { type: 'error' });
        }
    };

    useEffect(() => {
        const loadOrder = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_URL}/order-management/${orderId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setOrder(data.order);
                }
            } catch (error) {
                console.error('Failed to load order:', error);
            } finally {
                setLoading(false);
            }
        };

        loadOrder();
        loadInvoice();
    }, [orderId]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!order) {
        return (
            <Card>
                <CardContent>
                    <Typography color="error">Bestellung nicht gefunden</Typography>
                    <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mt: 2 }}>
                        Zurück zur Liste
                    </Button>
                </CardContent>
            </Card>
        );
    }

    return (
        <Box>
            <Title title={`Bestellung ${order.orderNumber}`} />

            {/* Breadcrumbs */}
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link
                    component="button"
                    underline="hover"
                    color="inherit"
                    onClick={onBack}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                    <ShoppingCartIcon fontSize="small" />
                    Bestellungen
                </Link>
                <Typography color="text.primary">{order.orderNumber}</Typography>
            </Breadcrumbs>

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <ReceiptIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h5">Bestellung {order.orderNumber}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {order.uuid}
                        </Typography>
                    </Box>
                </Box>
                <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
                    Zurück
                </Button>
            </Box>

            <Grid container spacing={3}>
                {/* Order Items */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Bestellpositionen</Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Produkt</TableCell>
                                            <TableCell>Typ</TableCell>
                                            <TableCell align="right">Einzelpreis</TableCell>
                                            <TableCell align="center">Menge</TableCell>
                                            <TableCell align="right">Rabatt</TableCell>
                                            <TableCell align="right">Gesamt</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {order.items.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{item.title}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={item.priceType === 'one_time' ? 'Einmalig' : item.priceType === 'subscription' ? 'Abo' : 'Kostenlos'}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">{formatCurrency(item.unitPrice, order.currency)}</TableCell>
                                                <TableCell align="center">{item.quantity}</TableCell>
                                                <TableCell align="right">
                                                    {parseFloat(item.discount || '0') > 0 ? `-${formatCurrency(item.discount, order.currency)}` : '-'}
                                                </TableCell>
                                                <TableCell align="right">{formatCurrency(item.total, order.currency)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Sidebar */}
                <Grid size={{ xs: 12, md: 4 }}>
                    {/* Summary */}
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ReceiptIcon /> Zusammenfassung
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <Grid container spacing={1}>
                                    <Grid size={{ xs: 6 }}>
                                        <Typography color="text.secondary">Zwischensumme:</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Typography align="right">{formatCurrency(order.subtotal, order.currency)}</Typography>
                                    </Grid>
                                    {parseFloat(order.discount || '0') > 0 && (
                                        <>
                                            <Grid size={{ xs: 6 }}>
                                                <Typography color="text.secondary">Rabatt:</Typography>
                                            </Grid>
                                            <Grid size={{ xs: 6 }}>
                                                <Typography align="right" color="success.main">-{formatCurrency(order.discount, order.currency)}</Typography>
                                            </Grid>
                                        </>
                                    )}
                                    {parseFloat(order.tax || '0') > 0 && (
                                        <>
                                            <Grid size={{ xs: 6 }}>
                                                <Typography color="text.secondary">MwSt:</Typography>
                                            </Grid>
                                            <Grid size={{ xs: 6 }}>
                                                <Typography align="right">{formatCurrency(order.tax, order.currency)}</Typography>
                                            </Grid>
                                        </>
                                    )}
                                    <Grid size={{ xs: 12 }}>
                                        <Divider sx={{ my: 1 }} />
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Typography fontWeight="bold">Gesamt:</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Typography align="right" fontWeight="bold" variant="h6">
                                            {formatCurrency(order.total, order.currency)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Status */}
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PaymentIcon /> Status
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="caption" color="text.secondary" display="block">Bestellstatus</Typography>
                                    <Chip
                                        label={statusConfig[order.status]?.label || order.status}
                                        color={statusConfig[order.status]?.color || 'default'}
                                        size="small"
                                    />
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="caption" color="text.secondary" display="block">Zahlungsstatus</Typography>
                                    <Chip
                                        label={paymentStatusConfig[order.paymentStatus]?.label || order.paymentStatus}
                                        color={paymentStatusConfig[order.paymentStatus]?.color || 'default'}
                                        size="small"
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="caption" color="text.secondary" display="block">Erstellt</Typography>
                                    <Typography variant="body2">{formatDate(order.createdAt)}</Typography>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="caption" color="text.secondary" display="block">Bezahlt</Typography>
                                    <Typography variant="body2">{formatDate(order.paidAt)}</Typography>
                                </Grid>
                                {order.completedAt && (
                                    <Grid size={{ xs: 12 }}>
                                        <Typography variant="caption" color="text.secondary" display="block">Abgeschlossen</Typography>
                                        <Typography variant="body2">{formatDate(order.completedAt)}</Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Customer */}
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon /> Kunde
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">{order.customerName}</Typography>
                            <Typography variant="body2" color="text.secondary">{order.customerEmail}</Typography>
                        </CardContent>
                    </Card>

                    {/* Invoice */}
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PictureAsPdfIcon /> Rechnung
                            </Typography>
                            {invoiceLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : invoice ? (
                                <Box>
                                    <Typography variant="body2" fontWeight="medium">
                                        {invoice.invoiceNumber}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {invoice.type === 'credit_note' ? 'Gutschrift' : 'Rechnung'} - {invoice.status === 'paid' ? 'Bezahlt' : invoice.status === 'issued' ? 'Ausgestellt' : 'Entwurf'}
                                    </Typography>
                                    {invoice.issuedAt && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Erstellt: {formatDate(invoice.issuedAt)}
                                        </Typography>
                                    )}
                                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<DownloadIcon />}
                                            onClick={downloadPdf}
                                        >
                                            PDF herunterladen
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Keine Rechnung vorhanden
                                    </Typography>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={creatingInvoice ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                                        onClick={createInvoice}
                                        disabled={creatingInvoice}
                                    >
                                        {creatingInvoice ? 'Wird erstellt...' : 'Rechnung erstellen'}
                                    </Button>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

// Main Orders Page Component
const OrdersPage: React.FC = () => {
    const { id } = useParams<{ id?: string }>();
    const navigate = useNavigate();

    const handleSelectOrder = (orderId: number) => {
        navigate(`/orders/${orderId}`);
    };

    const handleBack = () => {
        navigate('/orders');
    };

    if (id) {
        return <OrderDetail orderId={parseInt(id)} onBack={handleBack} />;
    }

    return <OrdersList onSelectOrder={handleSelectOrder} />;
};

export default OrdersPage;
